import OverwatchModal from "@/components/OverwatchModal";
import { Divider, Grid } from "@mui/material";
import { useState } from "react";
import { UploadedFile } from "@/Contexts/Funnel/types";
import { some } from "lodash";
import { objectLength, updateOnboardingCache } from "@/utils/helperFunctions";
import { useMutation } from "@apollo/client/react";
import { BulkImportResourcesDocument, CompanyResourceCreate, CompanyResourceResourceTypeEnum, CreateCompanyResourceDocument, OnboardingStepsEnum, UpdateOnboardingStepDocument } from "@/graphql/generated/graphql";
import { displayNotification } from "@/utils/notifications";
import { titleize } from "inflection";
import BulkImportSection from "./BulkImportSection";
import SingleResourceForm from "./SingleResourceForm";

interface BulkImportResourcesModalProps {
  importType: 'human' | 'equipment' | 'clients';
  handleClose: () => void;
}

const BulkImportResourcesModal: React.FC<BulkImportResourcesModalProps> = ({
  importType,
  handleClose
}) => (
  <BulkImportResourcesModalContent key={importType} importType={importType} handleClose={handleClose} />
);

const BulkImportResourcesModalContent: React.FC<BulkImportResourcesModalProps> = ({
  importType,
  handleClose,
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [companyResource, setCompanyResource] = useState<CompanyResourceCreate>(() => ({
    name: "",
    resourceType: importType === "equipment"
      ? CompanyResourceResourceTypeEnum.Equipment
      : CompanyResourceResourceTypeEnum.Human,
    resourceMeta: {},
  }));

  const [bulkImport, { loading: submitting }] = useMutation(BulkImportResourcesDocument,
    {
      onCompleted: () => {
        displayNotification({ type: "success", text: "File is being processed, You will be notify once it's completed." });
        handleCreate()
      },
      onError: (error) => displayNotification({ type: "error", text: error.message ? error.message : "Something went wrong please try again!" })
    }
  );

  const [createResource, { loading: creatingResource }] = useMutation(CreateCompanyResourceDocument, {
    refetchQueries: ["getCompanyResources"],
    onCompleted: () => {
      displayNotification({ type: "success", text: "Company Resource Created Successfully." });
      handleCreate();
    },
    onError: () => displayNotification({ type: "error", text: "Unable to create company resource." }),
  });

  // ======= Handling Bonboarding Step ==========
  const [updateOnboardingStep] = useMutation(UpdateOnboardingStepDocument);

  const handleCreate = () => {
    const step = importType === "human" ? OnboardingStepsEnum.HumanResourceCreated : OnboardingStepsEnum.EquipmentResourceCreated;

    updateOnboardingStep({
      variables: { step },
      onCompleted: () => handleClose(),
      update(cache, { data }) {
        updateOnboardingCache(cache, data?.updateOnboardingStep);
      },
    });
  }
  // ======= Handling Bonboarding Step ==========

  const handleSubmit = () => {
    if (!isUploadValid()) {
      if (companyResource) createResource({ variables: { companyResource } });
      return;
    }

    bulkImport({
      variables: {
        importType: importType === "clients" ? "clients" : `${importType}s`,
        signedId: files[0].signedBlobId as string
      }
    });
  };

  const isResourceValid = () => importType !== "clients" && !!companyResource?.name.trim();

  const isUploadValid = () => objectLength(files) === 1 && !some(files, file => file.uploadStatus !== "completed");

  const disableSave = () => !isResourceValid() && !isUploadValid();

  const resourceLabel = importType === "human" ? "user" : importType === "equipment" ? "equipment resource" : "client";

  return (
    <OverwatchModal
      size="lg"
      title={titleize(`Create ${importType} resources`)}
      caption="Create a single resource or import in bulk"
      handleClose={handleClose}
      primaryButtonAction={handleSubmit}
      primaryButtonText="Create"
      disablePrimary={disableSave() || submitting || creatingResource}
      hideSecondaryButton
    >
      <Grid container spacing={3} alignItems="stretch" sx={{ position: "relative" }}>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            display: { xs: "none", md: "block" },
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
          }}
        />
        <Grid size={{ xs: 12, md: 6 }}>
          <SingleResourceForm
            importType={importType}
            resourceLabel={resourceLabel}
            companyResource={companyResource}
            setCompanyResource={setCompanyResource}
          />
        </Grid>

        <Grid size={12} sx={{ display: { xs: "block", md: "none" } }}>
          <Divider />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <BulkImportSection importType={importType} files={files} setFiles={setFiles} />
        </Grid>
      </Grid>
    </OverwatchModal>
  );
};

export default BulkImportResourcesModal;
